import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
  PurchaseOrder, PurchaseOrderCreateDTO, PurchaseOrderMinorAmendmentDTO,
  PurchaseOrderResponse, PurchaseOrderStatus, BulkActionResult
} from '../interfaces/procurement/purchaseOrder.interface';

export const PurchaseOrderService = {
  listByInstitution: async (page = 0, size = 10, status?: PurchaseOrderStatus): Promise<PurchaseOrderResponse> => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    const statusParam = status ? `&status=${status}` : '';
    return (await axios.get(`${ENDPOINTS.PURCHASE_ORDER.LIST_BY_INSTITUTION(institutionId)}?page=${page}&size=${size}${statusParam}`)).data;
  },
  getById: async (id: string): Promise<PurchaseOrder> =>
    (await axios.get(ENDPOINTS.PURCHASE_ORDER.GET_BY_ID(id))).data,
  downloadPdf: async (id: string, poNumber?: string): Promise<void> => {
    const response = await axios.get(ENDPOINTS.PURCHASE_ORDER.PDF(id), {
      headers: AuthService.getAuthHeader(),
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${poNumber ?? `PO-${id}`}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  create: async (dto: PurchaseOrderCreateDTO): Promise<PurchaseOrder> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.CREATE, dto)).data,
  updateDraft: async (id: string, dto: PurchaseOrderCreateDTO): Promise<PurchaseOrder> =>
    (await axios.put(ENDPOINTS.PURCHASE_ORDER.UPDATE_DRAFT(id), dto)).data,
  submit: async (id: string): Promise<PurchaseOrder> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.SUBMIT(id))).data,
  approve: async (id: string): Promise<PurchaseOrder> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.APPROVE(id))).data,
  reject: async (id: string): Promise<PurchaseOrder> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.REJECT(id))).data,
  markOrdered: async (id: string): Promise<PurchaseOrder> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.MARK_ORDERED(id))).data,
  cancel: async (id: string): Promise<PurchaseOrder> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.CANCEL(id))).data,
  bulkApprove: async (ids: string[]): Promise<BulkActionResult> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.BULK_APPROVE, { ids })).data,
  bulkReject: async (ids: string[]): Promise<BulkActionResult> =>
    (await axios.post(ENDPOINTS.PURCHASE_ORDER.BULK_REJECT, { ids })).data,
  applyMinorAmendment: async (id: string, dto: PurchaseOrderMinorAmendmentDTO): Promise<PurchaseOrder> =>
    (await axios.put(ENDPOINTS.PURCHASE_ORDER.MINOR_AMENDMENT(id), dto)).data,
  applyMaterialAmendment: async (id: string, dto: PurchaseOrderCreateDTO): Promise<PurchaseOrder> =>
    (await axios.put(ENDPOINTS.PURCHASE_ORDER.AMEND(id), dto)).data,
  delete: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.PURCHASE_ORDER.DELETE(id)); },
};
